import { Application } from '@/types/application';
import { ComparerParam, ComparerPrompt, ComparerPromptGroup, ComparerPrompts } from '@/types/comparer';
import { Prompt, PromptGroup } from '@/types/prompt';
import { isNumber } from '@/util/typeGuards';
import { flattenValue, makeParam } from './comparerUtils';

const compareProps = <T>(itemA: T | undefined, itemB: T | undefined, keys: (keyof T & string)[]): ComparerParam[] =>
  keys.map((key) => makeParam(key, flattenValue(itemA?.[key]), flattenValue(itemB?.[key])));

const addPromptGroup = (
  promptA: PromptGroup | undefined,
  promptB: PromptGroup | undefined,
  id: number
): ComparerPromptGroup => {
  const changedProps = compareProps(promptA, promptB, ['name', 'path']);

  if (!promptA) return { status: 'added', id, label: promptB?.name || '', changedProps, prompts: [] };
  else if (!promptB) return { status: 'removed', id, label: promptA.name, changedProps, prompts: [] };

  return {
    status: changedProps.some((param) => param.changed) ? 'modified' : 'unchanged',
    id,
    label: promptA.name,
    changedProps,
    prompts: []
  };
};

const promptLabel = (id: number, prompt?: Prompt) => prompt?.label || prompt?.file || id.toString();
const addPrompt = (promptA: Prompt | undefined, promptB: Prompt | undefined, id: number): ComparerPrompt => {
  const changedProps = compareProps(promptA, promptB, ['file', 'label', 'path', 'promptGroup']);

  if (!promptA) return { status: 'added', id, label: promptLabel(id, promptB), changedProps };
  else if (!promptB) return { status: 'removed', id, label: promptLabel(id, promptA), changedProps };

  return {
    status: changedProps.some((param) => param.changed) ? 'modified' : 'unchanged',
    id,
    label: promptLabel(id, promptB),
    changedProps
  };
};

export const comparePrompts = (appA: Application, appB: Application): ComparerPrompts => {
  const groupMapA = new Map(appA.promptGroups?.map((group) => [group.id, group]));
  const groupMapB = new Map(appB.promptGroups?.map((group) => [group.id, group]));
  const allGroupIds = new Set([...groupMapA.keys(), ...groupMapB.keys()]);

  const groups = new Map<number, ComparerPromptGroup>();
  allGroupIds.forEach((groupId) => {
    const groupA = groupMapA.get(groupId);
    const groupB = groupMapB.get(groupId);

    groups.set(groupId, addPromptGroup(groupA, groupB, groupId));
  });

  const mapA = new Map(appA.prompts.map((prompt) => [prompt.id, prompt]));
  const mapB = new Map(appB.prompts.map((prompt) => [prompt.id, prompt]));
  const allPromptIds = new Set([...mapA.keys(), ...mapB.keys()]);

  const ungrouped: ComparerPrompt[] = [];
  allPromptIds.forEach((promptId) => {
    const promptA = mapA.get(promptId);
    const promptB = mapB.get(promptId);

    const comparedPrompt = addPrompt(promptA, promptB, promptId);
    comparedPrompt.changedProps = comparedPrompt.changedProps.map((param) =>
      param.key === 'promptGroup'
        ? {
            ...param,
            oldValue: isNumber(param.oldValue)
              ? (groupMapA.get(param.oldValue)?.name ?? param.oldValue)
              : param.oldValue,
            newValue: isNumber(param.newValue)
              ? (groupMapB.get(param.newValue)?.name ?? param.newValue)
              : param.newValue
          }
        : param
    );
    const groupId = promptB ? promptB.promptGroup : promptA?.promptGroup;
    const group = isNumber(groupId) ? groups.get(groupId) : undefined;

    if (group) group.prompts.push(comparedPrompt);
    else ungrouped.push(comparedPrompt);
  });

  return { groups: [...groups.values()], ungrouped };
};
